// Tablero gerencial: las ocho preguntas del intake, en una pantalla.
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Boton, EsqueletoTabla, Input, Pill, PuntoNaranja, TituloPagina, Vacio } from "@iavmx/ui";
import { leer } from "../api";
import { Barra, Segmentado } from "../componentes";
import { fechaHora, mxn, num, pct } from "../formato";
import { useNombresProducto } from "../datos";
import { useSesion } from "../sesion";
import { ETIQUETA_ETAPA, type RespuestaTablero } from "../tipos";

function Pregunta({ n, texto, children, ancha = false }: { n: number; texto: string; children: React.ReactNode; ancha?: boolean }) {
  return (
    <section className={`flex min-w-0 flex-col gap-3 border border-linea p-4 ${ancha ? "xl:col-span-2" : ""}`} aria-labelledby={`p${n}`}>
      <h2 id={`p${n}`} className="flex items-baseline gap-2 font-titulo text-base font-bold">
        <span className="font-ui text-xs text-tinta-tenue">{n}</span>
        {texto}
      </h2>
      {children}
    </section>
  );
}

function TablaMini({ cab, filas }: { cab: string[]; filas: (string | number | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full font-ui text-sm">
        <thead>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-[.08em] text-tinta-tenue">
            {cab.map((c, i) => (
              <th key={i} className={`py-1.5 ${i > 0 ? "pl-2 text-right" : ""}`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i} className="border-t border-linea">
              {f.map((c, j) => (
                <td key={j} className={`py-1.5 ${j > 0 ? "pl-2 text-right tabular-nums" : ""}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
          {filas.length === 0 && (
            <tr>
              <td colSpan={cab.length} className="py-3 text-center text-tinta-tenue">
                Nada que mostrar.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Tablero() {
  const { api, listo } = useSesion();
  const navegar = useNavigate();
  const b = useSearch({ from: "/tablero" });
  const nombreDe = useNombresProducto();
  const q = useQuery({
    queryKey: ["tablero", b.mes, b.ejecutivo],
    queryFn: () => api.api.tablero.$get({ query: { ...(b.mes ? { mes: b.mes } : {}), ...(b.ejecutivo ? { ejecutivo: b.ejecutivo } : {}) } }).then((r) => leer<RespuestaTablero>(r)),
    enabled: listo,
  });
  if (q.isLoading) return <EsqueletoTabla filas={8} columnas={3} />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar el tablero" texto={q.error?.message} />;
  const d = q.data;
  const P = d.preguntas;
  const vista: "equipo" | "vendedor" = b.ejecutivo ? "vendedor" : "equipo";
  const meta = P.ventaMes.meta;

  return (
    <>
      <TituloPagina
        descripcion={`Las ocho preguntas de Comercial, en una pantalla. Datos de Odoo y de esta plataforma al corte de ${fechaHora(d.corte)}.${d.gerencia ? "" : " Ves tu propia vista por vendedor."}`}
        accion={
          <div className="flex flex-wrap items-center gap-2">
            {d.gerencia && (
              <Segmentado
                etiqueta="Vista"
                valor={vista}
                opciones={[
                  { valor: "equipo", etiqueta: "Ejecutiva" },
                  { valor: "vendedor", etiqueta: "Por vendedor" },
                ]}
                onCambio={(v) => void navegar({ to: "/tablero", search: { mes: b.mes, ejecutivo: v === "vendedor" ? (b.ejecutivo ?? d.equipo.find((p) => p.rol === "ejecutivo")?.id) : undefined } })}
              />
            )}
            {d.gerencia && vista === "vendedor" && (
              <select value={b.ejecutivo ?? ""} onChange={(e) => void navegar({ to: "/tablero", search: { mes: b.mes, ejecutivo: e.target.value || undefined } })} className="h-8 border border-input bg-fondo px-2 font-ui text-xs" aria-label="Vendedor">
                {d.equipo.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            )}
            <label className="sr-only" htmlFor="mes">
              Mes
            </label>
            <Input id="mes" type="month" value={d.mes} onChange={(e) => void navegar({ to: "/tablero", search: { mes: e.target.value || undefined, ejecutivo: b.ejecutivo } })} className="h-8 w-40 text-base sm:text-xs" />
          </div>
        }
      >
        Tablero gerencial
        <PuntoNaranja />
      </TituloPagina>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Pregunta n={1} texto="¿Cuántas oportunidades tiene cada vendedor?">
          <TablaMini cab={["Ejecutivo", "Abiertas", "Lead", "Op.", "Prop.", "Neg.", "Gan.", "Perd."]} filas={P.porVendedor.map((v) => [v.nombre.split(" ")[0], v.abiertas, v.lead, v.oportunidad, v.propuesta, v.negociacion, v.ganado, v.perdido])} />
        </Pregunta>

        <Pregunta n={2} texto="¿Cuánto venderemos este mes?">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-titulo text-2xl font-black tabular-nums">{mxn(P.ventaMes.ponderado)}</div>
              <div className="font-ui text-xs text-tinta-tenue">ponderado por etapa</div>
            </div>
            <div>
              <div className="font-titulo text-2xl font-black tabular-nums">{mxn(P.ventaMes.cerrado)}</div>
              <div className="font-ui text-xs text-tinta-tenue">cerrado en {d.etiquetaMes.toLowerCase()}</div>
            </div>
          </div>
          <div className="font-ui text-sm text-tinta-3">{mxn(P.ventaMes.enNegociacion)} en negociación</div>
          {meta ? (
            <>
              <div className="flex justify-between font-ui text-xs text-tinta-tenue">
                <span>Meta {mxn(meta)}</span>
                <span>{pct(P.ventaMes.avancePct)}</span>
              </div>
              <Barra pct={P.ventaMes.avancePct} tono="tinta" etiqueta="Avance de meta" />
            </>
          ) : (
            <p className="font-ui text-xs text-tinta-tenue">Meta por definir (META_MENSUAL_MXN). Ponderado con la probabilidad de la oportunidad o la de su etapa.</p>
          )}
        </Pregunta>

        <Pregunta n={3} texto="¿Quién necesita apoyo?">
          {P.apoyo.length === 0 ? (
            <p className="font-ui text-sm text-tinta-tenue">Nadie con señales de alerta este mes.</p>
          ) : (
            <ul className="space-y-3">
              {P.apoyo.map((a) => (
                <li key={a.id} className="flex gap-3">
                  <Avatar nombre={a.nombre} className="size-7 text-[10px]" />
                  <div className="font-ui text-sm">
                    <div className="font-semibold">{a.nombre}</div>
                    <div className="text-tinta-3">{a.razones.join(", ")}.</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Pregunta>

        <Pregunta n={4} texto="¿Qué clientes u oportunidades están detenidos?" ancha>
          <TablaMini
            cab={["Cuenta", "Etapa", "Sin actividad", "Ejecutivo", "Valor"]}
            filas={P.detenidas.map((o) => [
              <Link key={o.id} to="/oportunidades/$id" params={{ id: o.id }} className="font-semibold hover:underline">
                {o.cuenta}
              </Link>,
              ETIQUETA_ETAPA[o.etapa],
              <span key="d" className={o.escalar ? "font-semibold" : ""}>
                {o.dias} días{o.vencida ? " · vencida" : ""}
              </span>,
              o.ejecutivo,
              o.valor ? mxn(o.valor) : "—",
            ])}
          />
          <p className="font-ui text-xs text-tinta-tenue">
            Detenida a los {d.config.diasInactividad} días sin actividad; alto valor (≥ {mxn(d.config.umbralAltoValor)}) escala a dirección a los {d.config.diasEscalamiento}.
          </p>
        </Pregunta>

        <Pregunta n={5} texto="¿Cuántas visitas y actividades realizó cada ejecutivo?">
          <ul className="space-y-2">
            {P.actividadesPorEjecutivo.map((a) => {
              const max = Math.max(1, ...P.actividadesPorEjecutivo.map((x) => x.total));
              return (
                <li key={a.id} className="grid grid-cols-[120px_minmax(0,1fr)_32px] items-center gap-2 font-ui text-sm">
                  <span className="truncate">{a.nombre}</span>
                  <Barra pct={(a.total / max) * 100} tono="tinta" etiqueta={`${a.total} actividades`} />
                  <span className="text-right tabular-nums">{a.total}</span>
                </li>
              );
            })}
          </ul>
          <TablaMini cab={["", "Llam.", "Visitas", "Reun.", "Prop."]} filas={P.actividadesPorEjecutivo.map((a) => [a.nombre.split(" ")[0], a.llamadas, a.visitas, a.reuniones, a.propuestas])} />
        </Pregunta>

        <Pregunta n={6} texto="¿Quién está cumpliendo metas?">
          {meta ? (
            <ul className="space-y-2">
              {P.metas.map((m) => (
                <li key={m.id} className="grid grid-cols-[120px_minmax(0,1fr)_48px] items-center gap-2 font-ui text-sm">
                  <span className="truncate">{m.nombre}</span>
                  <Barra pct={m.pct} tono={(m.pct ?? 0) >= 100 ? "tinta" : "azul"} etiqueta={`${m.pct ?? 0} % de meta`} />
                  <span className="text-right tabular-nums">{pct(m.pct)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <TablaMini cab={["Ejecutivo", "Cerrado"]} filas={P.metas.map((m) => [m.nombre, mxn(m.cerrado)])} />
          )}
          <p className="font-ui text-xs text-tinta-tenue">{meta ? "Cerrado del mes sobre meta individual (meta mensual entre vendedores). La línea completa es 100 %." : "Sin meta configurada: se muestra el cerrado del mes por vendedor."}</p>
        </Pregunta>

        <Pregunta n={7} texto="¿Qué oportunidades están en riesgo?">
          {P.enRiesgo.length === 0 ? (
            <p className="font-ui text-sm text-tinta-tenue">Ninguna en riesgo ahora mismo.</p>
          ) : (
            <ul className="divide-y divide-linea">
              {P.enRiesgo.map((x) => (
                <li key={`${x.tipo}-${x.id}`} className="py-2 font-ui text-sm">
                  <div className="flex justify-between gap-2">
                    {x.tipo === "oportunidad" ? (
                      <Link to="/oportunidades/$id" params={{ id: x.id }} className="font-semibold hover:underline">
                        {x.cuenta}
                      </Link>
                    ) : (
                      <Link to="/propuestas/$id" params={{ id: x.id }} className="font-semibold hover:underline">
                        {x.cuenta}
                      </Link>
                    )}
                    {x.valor && <span className="tabular-nums">{mxn(x.valor)}</span>}
                  </div>
                  <div className={`text-xs ${x.fuerte ? "font-semibold text-tinta" : "text-tinta-3"}`}>{x.motivo}</div>
                </li>
              ))}
            </ul>
          )}
        </Pregunta>

        <Pregunta n={8} texto="¿Cuál es la conversión por ejecutivo, etapa, producto y periodo?" ancha>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="mb-1 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Por etapa · últimos 90 días</div>
              <ul className="space-y-1.5">
                {P.conversion.embudo.map((e) => (
                  <li key={e.de} className="grid grid-cols-[150px_minmax(0,1fr)_40px] items-center gap-2 font-ui text-sm">
                    <span>
                      {ETIQUETA_ETAPA[e.de as keyof typeof ETIQUETA_ETAPA]} → {ETIQUETA_ETAPA[e.a as keyof typeof ETIQUETA_ETAPA]}
                    </span>
                    <Barra pct={e.pct} etiqueta={`${e.pct ?? 0} %`} />
                    <span className="text-right tabular-nums">{pct(e.pct)}</span>
                  </li>
                ))}
              </ul>
              <div className="mb-1 mt-4 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Por periodo</div>
              <TablaMini cab={["Mes", "Ganadas", "Perdidas", "Conv."]} filas={P.conversion.porPeriodo.map((p) => [p.periodo, p.ganadas, p.perdidas, pct(p.pct)])} />
            </div>
            <div>
              <div className="mb-1 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Por ejecutivo · {d.etiquetaMes.toLowerCase()}</div>
              <TablaMini cab={["Ejecutivo", "Ganadas", "Perdidas", "Conv."]} filas={P.conversion.porEjecutivo.map((p) => [p.nombre, p.ganadas, p.perdidas, pct(p.pct)])} />
              <div className="mb-1 mt-4 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Por producto</div>
              <TablaMini cab={["Producto", "Propuestas", "Ganadas", "Conv."]} filas={P.conversion.porProducto.map((p) => [nombreDe(p.producto), p.propuestas, p.ganadas, pct(p.pct)])} />
            </div>
          </div>
        </Pregunta>
      </div>
      <p className="mt-6 font-ui text-xs text-tinta-tenue">
        {num(d.equipo.length)} personas en Comercial.{" "}
        <Boton variante="liga" tamano="sm" asChild>
          <Link to="/">Ir al pipeline</Link>
        </Boton>
        {d.vendedor && <Pill tono="neutro" className="ml-2">Vista de {d.vendedor.nombre}</Pill>}
      </p>
    </>
  );
}
